'use client'

import UIFlex from '@components/ui/UIFlex'
import { pxToRem } from '@ui/style/muiTheme'
import { Icon32Comment } from 'src/assets/icons/Icon32Comment'
import { Icon32TextSize } from 'src/assets/icons/Icon32TextSize'
import { Icon32Print } from 'src/assets/icons/Icon32Print'
import UIDivider from '@components/ui/UIDivider'
import UIIconButton from '@components/ui/UIIconButton'
import { Icon32Share } from 'src/assets/icons/Icon32Share'
import { useRef, useState } from 'react'
import { Box } from '@mui/joy'
import useClickOutside from '@components/common/Popover/useClickOutisde'
import PillButton from '@components/button/PillButton'
import { ArticleResponseVo } from '@schemas/non-auth'
import ScrapButton from '@components/button/ScrapButton'
import { useArticleSizeControl } from '@store/articleSize'
import SharePopover from '@components/common/Popover/shared/SharePopover'
import AIPopover from './AIPopover'
import { useIsCommentDrawControl } from '@store/isCommentDraw'
import useShareControl from '@hooks/useShareControl'
import UIText from '@components/ui/UIText'
import { isMobile } from 'react-device-detect'
import CommentCountBadge from '@components/common/Comments/CommentCountBadge'

interface ArticleUtilityProps {
	article: ArticleResponseVo
	isDark?: boolean
	scrap?: boolean
	commentVisible: boolean
	setScrap: (value: boolean) => void
}

const ArticleUtility = ({
	article,
	isDark,
	scrap,
	commentVisible,
	setScrap,
}: ArticleUtilityProps) => {
	// 공유하기 Popover 제어
	// AI요약 Popover 제어
	const { nextArticleSize } = useArticleSizeControl()
	const { setIsCommentDraw } = useIsCommentDrawControl()
	const { isAppShare } = useShareControl()
	const [showSharePopover, setShowSharePopover] = useState(false)
	const sharePopoverRef = useRef<HTMLDivElement>(null)
	const [showAIPopover, setShowAIPopover] = useState(false)
	const AIPopoverRef = useRef<HTMLDivElement>(null)

	const handleCommentOpen = () => {
		setIsCommentDraw(true)
	}
	const handlePrint = () => {
		window.print()
	}
	const openSharePopover = () => {
		if (isAppShare()) {
			setShowSharePopover(true)
			setShowAIPopover(false)
		}
	}
	const closeSharePopover = () => {
		setShowSharePopover(false)
	}
	useClickOutside(sharePopoverRef, closeSharePopover)

	const openAIPopover = () => {
		setShowAIPopover(true)
		setShowSharePopover(false)
	}
	const closeAIPopover = () => {
		setShowAIPopover(false)
	}
	useClickOutside(AIPopoverRef, closeAIPopover)

	return (
		<UIFlex
			flexDirection="row"
			justifyContent={{ xs: 'space-between', md: 'flex-end' }}
			flexShrink="0"
			alignContent="center"
		>
			{article.articleSummaryContent && article.articleSummaryContent !== '' && (
				<>
					<Box position="relative">
						<PillButton
							size="lg"
							btncolor={isDark ? 'dark' : 'neutral'}
							onClick={openAIPopover}
							aria-expanded={showAIPopover}
							sx={{ height: pxToRem(36) }}
						>
							요약
						</PillButton>
						<AIPopover
							ref={AIPopoverRef}
							content={article.articleSummaryContent ?? undefined}
							open={showAIPopover}
							onClose={closeAIPopover}
						/>
					</Box>
					<UIDivider
						orientation="vertical"
						isDark={isDark}
						sx={{
							height: pxToRem(24),
							display: { xs: 'none', md: 'block' },
							ml: pxToRem(15),
							mr: pxToRem(8),
							my: 'auto',
						}}
					/>
				</>
			)}
			<UIFlex
				className="print-hide"
				flexDirection="row"
				ml="auto"
				sx={{
					'&>*:not(:first-child)': {
						marginLeft: pxToRem(4),
					},
				}}
			>
				{commentVisible && (
					<UIIconButton color={isDark ? 'dark' : 'neutral'} onClick={handleCommentOpen}>
						<Icon32Comment isDark={isDark} />
						<CommentCountBadge id={article.articleIdx} />
						<UIText readonly>댓글보기</UIText>
					</UIIconButton>
				)}
				<Box position="relative" fontSize="0">
					<UIIconButton
						color={isDark ? 'dark' : 'neutral'}
						onClick={openSharePopover}
						aria-expanded={showSharePopover}
					>
						<Icon32Share isDark={isDark} />
						<UIText readonly>공유하기</UIText>
					</UIIconButton>
					<SharePopover
						ref={sharePopoverRef}
						open={showSharePopover}
						onClose={closeSharePopover}
					/>
				</Box>
				<ScrapButton
					large
					articleIdx={article.articleIdx}
					isScrap={scrap ?? article.isScrap ?? false}
					isDark={isDark}
					onChange={setScrap}
				/>
				<UIIconButton color={isDark ? 'dark' : 'neutral'} onClick={nextArticleSize}>
					<Icon32TextSize isDark={isDark} />
					<UIText readonly>글자 크기 변경</UIText>
				</UIIconButton>
				{!isMobile && (
					<UIIconButton
						color={isDark ? 'dark' : 'neutral'}
						sx={{ display: { xs: 'none', md: 'flex' } }}
						onClick={handlePrint}
					>
						<Icon32Print isDark={isDark} />
						<UIText readonly>인쇄하기</UIText>
					</UIIconButton>
				)}
			</UIFlex>
		</UIFlex>
	)
}

export default ArticleUtility
